import { useMemo, useState } from "react";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import {
  formatBrl,
  planForward,
  resolveEffectiveConfig,
  type ChannelOverride,
  type ForwardDropReason,
  type ForwardingSettings,
} from "@lucralink/shared";
import { cn } from "@/lib/utils";
import { SAMPLE_OFFER_TEXT } from "@/lib/forwarding";
import type { ForwardingChannel } from "@/lib/hooks/useForwarding";
import { Sheet, SheetSection } from "./Sheet";
import { selectClassName } from "./controls";

export const DROP_REASON_LABEL: Readonly<Record<ForwardDropReason, string>> = {
  no_link_after_terms: "Nenhum link válido sobrou depois de apagar as linhas com termos ignorados.",
  price_unknown: "O preço não foi encontrado e o filtro está configurado para descartar.",
  below_min_price: "O preço está abaixo do mínimo do filtro por valor.",
  above_max_price: "O preço está acima do máximo do filtro por valor.",
  below_min_discount: "O desconto está abaixo do mínimo configurado.",
  discount_unknown: "O desconto não foi encontrado e o filtro está configurado para descartar.",
  outside_window: "Fora da agenda de envios, e ofertas fora do horário são descartadas.",
};

interface FilterSimulatorProps {
  readonly open: boolean;
  readonly settings: ForwardingSettings;
  readonly sources: readonly ForwardingChannel[];
  readonly overrides: readonly ChannelOverride[];
  readonly onClose: () => void;
}

function Verdict({ plan }: { plan: ReturnType<typeof planForward> }): JSX.Element {
  if (plan.action === "drop") {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        <XCircle size={16} className="mt-0.5 shrink-0" />
        <span>
          <strong className="block">Seria descartada</strong>
          {DROP_REASON_LABEL[plan.reason]}
        </span>
      </div>
    );
  }
  const queued = plan.action === "queue";
  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-lg border p-3 text-sm",
        queued
          ? "border-amber-400/30 bg-amber-400/10 text-amber-200"
          : "border-primary/30 bg-primary/10 text-primary",
      )}
    >
      {queued ? (
        <Clock size={16} className="mt-0.5 shrink-0" />
      ) : (
        <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
      )}
      <span>
        <strong className="block">{queued ? "Ficaria na fila" : "Seria encaminhada agora"}</strong>
        {queued
          ? `Fora da agenda: sai em ${plan.releaseAt.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}.`
          : `${plan.acceptedUrls.length} link(s) seriam convertidos.`}
      </span>
    </div>
  );
}

type Plan = ReturnType<typeof planForward>;

function SampleInput({
  text,
  sourceKey,
  sources,
  onText,
  onSource,
}: {
  text: string;
  sourceKey: string;
  sources: readonly ForwardingChannel[];
  onText: (v: string) => void;
  onSource: (v: string) => void;
}): JSX.Element {
  return (
    <SheetSection title="Mensagem de exemplo">
      <textarea
        aria-label="Mensagem de exemplo"
        rows={6}
        className="w-full resize-y rounded-md border border-border bg-background px-3 py-2 font-mono text-xs text-foreground outline-none focus:border-primary/50"
        value={text}
        onChange={(e) => onText(e.target.value)}
      />
      <select
        aria-label="Origem simulada"
        className={selectClassName}
        value={sourceKey}
        onChange={(e) => onSource(e.target.value)}
      >
        <option value="">Só o padrão global</option>
        {sources.map((s) => (
          <option key={s.id} value={`${s.platform}:${s.ref}`}>
            {s.title ?? s.ref}
          </option>
        ))}
      </select>
    </SheetSection>
  );
}

function SimulatedLine({ line, term }: { line: string; term: string | undefined }): JSX.Element {
  return (
    <li
      className={cn(
        "flex items-start justify-between gap-3",
        term ? "text-destructive line-through decoration-destructive/60" : "text-foreground",
      )}
    >
      <span className="break-all">{line || " "}</span>
      {term && (
        <span className="shrink-0 rounded bg-destructive/15 px-1.5 text-[10px] no-underline">
          termo: {term}
        </span>
      )}
    </li>
  );
}

function LineByLine({ text, plan }: { text: string; plan: Plan }): JSX.Element {
  const removed = new Map(plan.removed.map((r) => [r.index, r.term]));
  return (
    <SheetSection title="Linha a linha">
      <ol className="space-y-1 rounded-lg border border-border bg-[hsl(var(--surface)/0.6)] p-3 font-mono text-xs">
        {text.split(/\r?\n/).map((line, index) => (
          <SimulatedLine key={index} line={line} term={removed.get(index)} />
        ))}
      </ol>
    </SheetSection>
  );
}

function OfferReading({ offer }: { offer: Plan["offer"] }): JSX.Element {
  return (
    <SheetSection title="O que foi lido da oferta">
      <dl className="grid grid-cols-2 gap-2 text-xs">
        <dt className="text-muted-foreground">Preço</dt>
        <dd className="text-foreground">
          {offer.priceCents === null ? "não encontrado" : formatBrl(offer.priceCents)}
        </dd>
        <dt className="text-muted-foreground">Preço original</dt>
        <dd className="text-foreground">
          {offer.originalPriceCents === null ? "-" : formatBrl(offer.originalPriceCents)}
        </dd>
        <dt className="text-muted-foreground">Desconto</dt>
        <dd className="text-foreground">
          {offer.discountPercent === null ? "-" : `${offer.discountPercent}%`}
        </dd>
        <dt className="text-muted-foreground">Cupom</dt>
        <dd className="text-foreground">{offer.coupon ?? "-"}</dd>
      </dl>
    </SheetSection>
  );
}

/** Simulador: cola uma mensagem e ve, linha a linha, o que os filtros fazem. */
export function FilterSimulator({
  open,
  settings,
  sources,
  overrides,
  onClose,
}: FilterSimulatorProps): JSX.Element | null {
  const [text, setText] = useState(SAMPLE_OFFER_TEXT);
  const [sourceKey, setSourceKey] = useState("");
  const plan = useMemo(() => {
    const override = overrides.find((o) => `${o.platform}:${o.channelRef}` === sourceKey) ?? null;
    return planForward({
      text,
      config: resolveEffectiveConfig(settings, override),
      now: new Date(),
    });
  }, [text, sourceKey, settings, overrides]);

  return (
    <Sheet
      open={open}
      title="Simulador de filtros"
      description="Nada é enviado: é só para testar o padrão global e os ajustes por origem."
      onClose={onClose}
    >
      <SampleInput
        text={text}
        sourceKey={sourceKey}
        sources={sources}
        onText={setText}
        onSource={setSourceKey}
      />
      <LineByLine text={text} plan={plan} />
      <OfferReading offer={plan.offer} />
      <SheetSection title="Resultado">
        <Verdict plan={plan} />
      </SheetSection>
    </Sheet>
  );
}
